const DELETE_SPEED_MS = 30;
const TYPE_SPEED_MS = 60;

/**
 * Where slide `i` has to sit, in slide-widths, for the window at `step` to
 * show slide `step % count` with a real slide on either side of it.
 *
 * This is how embla loops without clones (D92): the track keeps sliding one
 * width per tick forever, and any slide that has fallen a full lap behind the
 * window is lifted a lap forward by its own transform. Nothing is duplicated,
 * so the served HTML carries each slide's copy exactly once — which is the
 * point: crawlers and AI readers used to see six slides for four.
 *
 * @param {number} step  The window position, in slides.
 * @param {number} index The slide index.
 * @param {number} count The number of slides.
 */
function lapOffset( step, index, count ) {
	return Math.round( ( step - index ) / count ) * count;
}

function initShowcase( root ) {
	const track = root.querySelector( '.carousel-track' );
	if ( ! track ) {
		return;
	}

	const wordEl = root.querySelector( '.carousel-showcase-word' );
	if ( ! wordEl ) {
		return;
	}

	const subtitleEl = root.querySelector( '.carousel-showcase-subtitle' );
	if ( ! subtitleEl ) {
		return;
	}

	const slides = Array.from( track.children );
	if ( slides.length === 0 ) {
		return;
	}

	const slidesData = slides.map( ( slide ) => ( {
		title: slide.dataset.title || '',
		subtitle: slide.dataset.subtitle || '',
	} ) );

	slides.forEach( ( slide ) => slide.classList.add( 'carousel-slide' ) );

	const count = slides.length;
	const loop = root.dataset.loop !== 'false' && count > 1;
	const autoplay = root.dataset.autoplay !== 'false' && count > 1;
	const delay = parseInt( root.dataset.autoplayDelay, 10 ) || 4000;
	const pauseOnHover = root.dataset.pauseOnHover !== 'false';
	// Reduced motion (T-085): the track does not auto-advance and the heading
	// word is written in full instead of typed. The block has no manual
	// controls, so the first slide and its title are the static state.
	const reducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	);

	const cursorEl = root.querySelector( '.carousel-showcase-cursor' );
	cursorEl?.classList.add(
		'animate-[blink-cursor_0.8s_step-end_infinite]',
		'motion-reduce:animate-none'
	);

	let step = 0;
	let currentWord = '';
	let lastTitle = null;
	let typeTimeoutId = null;
	let autoplayTimerId = null;

	function render() {
		if ( loop ) {
			slides.forEach( ( slide, index ) => {
				const lap = lapOffset( step, index, count );
				slide.style.transform = lap
					? `translateX(${ lap * 100 }%)`
					: '';
			} );
		}
		track.style.transform = `translateX(-${ step * 100 }%)`;
	}

	function playChromaSweep() {
		wordEl.classList.remove( 'chroma-sweep-animate', 'text-grey-100' );
		void wordEl.offsetWidth;
		wordEl.classList.add( 'chroma-text', 'chroma-sweep-animate' );
	}

	function typeForward( target, i ) {
		currentWord = target.slice( 0, i );
		wordEl.textContent = currentWord;
		if ( i < target.length ) {
			typeTimeoutId = setTimeout(
				() => typeForward( target, i + 1 ),
				TYPE_SPEED_MS
			);
		} else {
			playChromaSweep();
		}
	}

	function deleteBackward( target ) {
		if ( currentWord.length > 0 ) {
			currentWord = currentWord.slice( 0, -1 );
			wordEl.textContent = currentWord;
			typeTimeoutId = setTimeout(
				() => deleteBackward( target ),
				DELETE_SPEED_MS
			);
		} else {
			typeForward( target, 1 );
		}
	}

	function setWord( target ) {
		clearTimeout( typeTimeoutId );
		wordEl.classList.remove( 'chroma-text', 'chroma-sweep-animate' );
		wordEl.classList.add( 'text-grey-100' );
		if ( reducedMotion.matches ) {
			currentWord = target;
			wordEl.textContent = target;
			// chroma.css settles the sweep to its end state under reduced motion.
			playChromaSweep();
			return;
		}
		if ( currentWord.length === 0 ) {
			typeForward( target, 1 );
		} else {
			deleteBackward( target );
		}
	}

	// Title/subtitle only ever update here, which only ever runs on an
	// autoplay tick — so pausing autoplay on hover pauses the heading
	// animation for free.
	function updateHeading() {
		const data = slidesData[ step % count ];
		if ( ! data ) {
			return;
		}
		subtitleEl.textContent = data.subtitle;
		if ( data.title === lastTitle ) {
			return;
		}
		lastTitle = data.title;
		setWord( data.title );
	}

	function advance() {
		if ( loop ) {
			step += 1;
		} else {
			step = Math.min( step + 1, count - 1 );
		}
		render();
		updateHeading();
	}

	function startAutoplay() {
		if ( ! autoplay || reducedMotion.matches ) {
			return;
		}
		stopAutoplay();
		autoplayTimerId = setInterval( advance, delay );
	}

	function stopAutoplay() {
		if ( autoplayTimerId ) {
			clearInterval( autoplayTimerId );
			autoplayTimerId = null;
		}
	}

	if ( pauseOnHover && count > 1 ) {
		root.addEventListener( 'mouseenter', stopAutoplay );
		root.addEventListener( 'mouseleave', startAutoplay );
	}

	render();
	updateHeading();
	startAutoplay();

	// The setting can change while the page is open.
	reducedMotion.addEventListener( 'change', () => {
		if ( reducedMotion.matches ) {
			stopAutoplay();
		} else {
			startAutoplay();
		}
	} );
}

document.addEventListener( 'DOMContentLoaded', () => {
	document
		.querySelectorAll( '.wp-block-avoid-content-carousel-showcase' )
		.forEach( initShowcase );
} );
